"use client";
import { SchoolData } from "@nimble/candidate-api/types";
import { WhenAllReady } from "@nimble/react-requests/components/shared/when-all-ready";
import { getQueryData } from "@nimble/react-requests/data/response-utils";
import { Lock, Unlock } from "lucide-react";
import type { JSX } from "react";

import {
  PrincipalCard,
  PrincipalCardPreset,
} from "@/app/(discover)/candidate/invites/components/principal-card";
import {
  normalizeBrToNewline,
  truncateToPreviewLength,
} from "@/app/(discover)/candidate/invites/lib/invites-page.utils";
import { MessageState } from "@/app/(discover)/candidate/invites/lib/invites-types";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import useBatchedSchoolData from "@/hooks/data/use-batched-school-data";
import { MatchStatusEnum } from "@/lib/api-clients/recommendation-api-client";
import { cn } from "@/lib/utils";

function LockedAvatar(): JSX.Element {
  return (
    <div className="w-12 h-12 rounded-full flex items-center justify-center bg-muted">
      <Lock className="w-5 h-5 text-muted-foreground" />
    </div>
  );
}

function RespondedBadge(): JSX.Element {
  return (
    <span className="text-xs text-[var(--invite-responded-badge)] font-medium flex-shrink-0 ml-2">
      Responded
    </span>
  );
}

/**
 * Stages a principal can advance a mutual connection to. Every other status —
 * the double-opt-in ones, and anything on the invitations tab — carries no tag.
 */
const PIPELINE_STAGES: Partial<
  Record<MatchStatusEnum, { label: string; className: string }>
> = {
  [MatchStatusEnum.INVITED]: {
    label: "Invited",
    className: "bg-info-soft text-info",
  },
  [MatchStatusEnum.HIRED]: {
    label: "Hired",
    className: "bg-success-soft text-success",
  },
};

function PipelineStageBadge({
  matchStatus,
}: {
  matchStatus: MatchStatusEnum;
}): JSX.Element | null {
  const stage = PIPELINE_STAGES[matchStatus];
  if (!stage) {
    return null;
  }

  return (
    <Badge className={cn("ml-2 flex-shrink-0", stage.className)}>
      {stage.label}
    </Badge>
  );
}

function UnlockIndicator(): JSX.Element {
  return (
    <div className="flex items-center gap-1.5 mt-2">
      <Unlock className="w-3 h-3 text-brand-purple" />
      <p className="text-xs text-brand-purple font-medium">
        Respond to unlock next invitation
      </p>
    </div>
  );
}

function LockedExplanation(): JSX.Element {
  return (
    <p className="text-xs text-muted-foreground/70 mt-1">
      Respond above to unlock
    </p>
  );
}

function MessagePreview({
  text,
  locked,
}: {
  text: string;
  locked: boolean;
}): JSX.Element {
  const finalText = locked
    ? "Reply to unlock this invitation"
    : normalizeBrToNewline(text);

  return (
    <p
      className={cn([
        "text-xs truncate",
        locked
          ? "text-[var(--invite-locked-text)] blur-[2px]"
          : "text-muted-foreground",
      ])}
    >
      {finalText}
    </p>
  );
}

const buttonClass = "w-full text-left border-b-1 p-4 transition-all";

function MessageLoader() {
  return (
    <button className={buttonClass} disabled>
      <div className="flex flex-row gap-2 overflow-hidden">
        <Skeleton className="flex-0 shrink-0 basis-auto h-12 w-12 rounded-full" />
        <div className="flex flex-col gap-2">
          <Skeleton className="h-4 w-50" />
          <Skeleton className="h-4 w-70" />
          <Skeleton className="h-4 w-full" />
        </div>
      </div>
    </button>
  );
}

export function ChatMessageView({
  previewMessage,
  schoolData,
  onSelect,
  invitationState,
  matchStatus,
  hasLockedInvitations = false,
}: {
  previewMessage: string | null | undefined;
  schoolData: SchoolData;
  onSelect: () => void;
  invitationState: MessageState;
  matchStatus: MatchStatusEnum;
  hasLockedInvitations?: boolean;
}) {
  const locked = invitationState === "locked";
  const responded = invitationState === "responded";
  const selected = invitationState === "selected";
  const available = invitationState === "available";

  const isDisabled = responded || locked;

  const avatar = locked ? (
    <LockedAvatar />
  ) : (
    <PrincipalCard.Avatar schoolData={schoolData} className="w-12 h-12" />
  );

  return (
    <button
      onClick={onSelect}
      className={cn([
        buttonClass,
        available && "bg-card hover:bg-surface-subtle",
        locked && "bg-surface-subtle opacity-60",
        responded && "bg-card opacity-60",
        selected && "bg-surface-subtle border-l-2 border-surface-inverted",
      ])}
      disabled={isDisabled}
    >
      <PrincipalCardPreset
        schoolData={schoolData}
        avatar={avatar}
        extraName={
          // The invitations tab shares this component and relies on the responded
          // badge, so that keeps the slot wherever it applies.
          responded ? (
            <RespondedBadge />
          ) : (
            <PipelineStageBadge matchStatus={matchStatus} />
          )
        }
        extraMessage={
          <>
            {previewMessage && (
              <MessagePreview
                text={truncateToPreviewLength(previewMessage)}
                locked={locked}
              />
            )}
            {hasLockedInvitations && (available || selected) && (
              <UnlockIndicator />
            )}
            {locked && <LockedExplanation />}
          </>
        }
      />
    </button>
  );
}

export default function ChatMessageContainer({
  onSelect,
  messageState,
  matchStatus,
  schoolNcesId,
  previewMessage,
  hasLockedInvitations = false,
}: {
  schoolNcesId: string;
  previewMessage: string | null | undefined;
  onSelect: () => void;
  messageState: MessageState;
  matchStatus: MatchStatusEnum;
  hasLockedInvitations?: boolean;
}) {
  const schoolQuery = useBatchedSchoolData({ ncesId: schoolNcesId });
  const schoolData = getQueryData(schoolQuery);

  return (
    <WhenAllReady queries={[schoolQuery]} loader={<MessageLoader />}>
      {schoolData && (
        <ChatMessageView
          invitationState={messageState}
          matchStatus={matchStatus}
          previewMessage={previewMessage}
          schoolData={schoolData}
          onSelect={onSelect}
          hasLockedInvitations={hasLockedInvitations}
        />
      )}
    </WhenAllReady>
  );
}
